<script setup lang="ts">
import { computed } from 'vue';
import type { CaseHit, HitRecordKind, HitVerdict } from './caseDetail';
import HitCategoryTag from './HitCategoryTag.vue';
import HitFilterTabs, { type HitFilterKey } from './HitFilterTabs.vue';

const props = defineProps<{
  hits: CaseHit[];
  selectedId: string;
  // The reviewer's own recorded verdict per hit (aml-review-console's
  // `overrides`) — drives the verdict dot and the Undecided filter.
  decisions: Record<string, HitVerdict>;
  notes: Record<string, string>;
  // Hit ids counted as "decided" case-wide — the union of a reviewer
  // override and a bot-resolved hit (see AmlReviewCasePage.vue's
  // `decidedCount`), so this filter and the app bar's "Cleared" count
  // never disagree about which hits still need attention.
  decidedIds: string[];
}>();

const emit = defineEmits<{
  select: [hitId: string];
}>();

// Genuinely backed by the real per-hit reviewer decision (see the
// 2026-08-07 decision superseding the 2026-08-04 "reflect only real
// fields" call, which was about the *design's* category-only row at the
// time, not a ban on this dimension). "Undecided" leads and is the
// default — a reviewer opens a case to work through what's left, not to
// re-read hits already decided (v4's own literal order/default is
// reversed, "All hits" first; overridden per direct feedback). Shared with
// the mobile hit-list screen via HitFilterTabs so both stay in sync.
const activeFilter = defineModel<HitFilterKey>('filter', { default: 'undecided' });

const decidedIdSet = computed(() => new Set(props.decidedIds));

const filteredHits = computed(() =>
  props.hits.filter((hit) => activeFilter.value === 'all' || !decidedIdSet.value.has(hit.id))
);

const decidedCount = computed(() => props.hits.filter((h) => decidedIdSet.value.has(h.id)).length);
const progressPct = computed(() =>
  props.hits.length === 0 ? 0 : Math.round((decidedCount.value / props.hits.length) * 100)
);

function hasUnsavedNote(hitId: string): boolean {
  return Boolean(props.decisions[hitId] && !(props.notes[hitId] ?? '').trim());
}

const verdictDotStyles: Record<HitVerdict, string> = {
  true_positive: 'bg-red-500',
  false_positive: 'bg-green-500',
  unknown: 'bg-amber-500',
};

function recordCountLabel(hit: CaseHit): string {
  const n = hit.records?.length ?? 0;
  return n === 1 ? '1 record' : `${n} records`;
}

type RecordCategory = 'pep' | 'sanction' | 'adverseMedia';
const CATEGORY_BY_KIND: Record<HitRecordKind, RecordCategory | undefined> = {
  PEP_POSITION: 'pep',
  SANCTION: 'sanction',
  ARTICLE: 'adverseMedia',
  IDENTIFIER: undefined,
  SOURCE: undefined,
  PROFILE: undefined,
};
const CANONICAL_HIT_TYPE_BY_RECORD_CATEGORY: Record<RecordCategory, string> = {
  pep: 'pep',
  sanction: 'sanctions',
  adverseMedia: 'adversemedia',
};

function hitTypeTags(hit: CaseHit): string[] {
  if (hit.hitTypes.length) return hit.hitTypes;
  const categories = new Set<string>();
  for (const record of hit.records ?? []) {
    const category = CATEGORY_BY_KIND[record.kind];
    if (category) categories.add(CANONICAL_HIT_TYPE_BY_RECORD_CATEGORY[category]);
  }
  return [...categories];
}
</script>

<template>
  <aside
    class="hidden w-[296px] flex-none flex-col border-r border-gray-200 bg-white lg:flex"
    data-test="hit-rail"
  >
    <div class="flex flex-none flex-col gap-2.5 border-b border-gray-200 px-3.5 py-3">
      <div class="flex items-baseline justify-between">
        <h2 class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Hits</h2>
        <span class="text-[11.5px] font-medium text-gray-400"
          >{{ decidedCount }}/{{ hits.length }}</span
        >
      </div>
      <div class="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          class="h-full rounded-full bg-indigo-600 transition-[width]"
          :style="{ width: `${progressPct}%` }"
        ></div>
      </div>
      <HitFilterTabs v-model="activeFilter" />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <button
        v-for="hit in filteredHits"
        :key="hit.id"
        type="button"
        class="flex w-full flex-col gap-1 border-b border-l-[3px] border-gray-100 px-3.5 py-2.5 text-left"
        :class="
          hit.id === selectedId
            ? 'border-l-indigo-600 bg-indigo-500/10'
            : 'border-l-transparent hover:bg-gray-50'
        "
        @click="emit('select', hit.id)"
      >
        <div class="flex flex-wrap items-center gap-1.5">
          <HitCategoryTag v-for="hitType in hitTypeTags(hit)" :key="hitType" :hit-type="hitType" />
        </div>
        <div class="flex min-w-0 items-center gap-1.5">
          <span class="min-w-0 flex-1 truncate text-[11px] font-medium text-gray-400">{{
            hit.id
          }}</span>
          <span
            v-if="hasUnsavedNote(hit.id)"
            class="flex-none font-mono text-xs text-amber-600"
            title="Decided but no note yet"
            >✎</span
          >
          <span
            v-if="decisions[hit.id]"
            class="h-1.5 w-1.5 flex-none rounded-full"
            :class="verdictDotStyles[decisions[hit.id]]"
          ></span>
        </div>
        <span class="truncate text-[13px] font-semibold text-gray-900">{{ hit.matchedName }}</span>
        <span class="text-[11.5px] text-gray-500">{{ recordCountLabel(hit) }}</span>
      </button>
      <p v-if="filteredHits.length === 0" class="p-4 text-center text-xs text-gray-400">
        No hits match this filter.
      </p>
    </div>
  </aside>
</template>
